import { normalizeCustomGroups, type LensCustomGroup } from './custom-groups';
import { normalizeListColumns, type ListColumn } from './list-view';
import { normalizeView, type MapView } from './map-view';
import { mergeRankPatch, normalizeRankMap, type RankMap, type RankPatch } from './rank-order';
import { normalizeCriteria, SELF_VALUE, type CriteriaOp, type Criterion } from './search-criteria';

/**
 * A **lens**: the serializable config a `views` row of kind `lens` carries
 * (docs/LENSES.md §2 - the way-of-looking kind). A lens IS its config and
 * nothing else: criteria + per-rail arrangement + a default rail. By kind it
 * has **no membership, no item reach, no flow link, and confers no access**
 * (law 1: config never resolves access) - it always evaluates through the
 * opener's own grants ("these criteria, through YOUR eyes"), so unlike a
 * board payload there is no membership `source`, no pins, no `hidden` set,
 * and no per-item crumbs here. Compare `BoardView` (the board kind, which
 * DOES hold membership) - the split this kind exists to make honest.
 *
 * The per-rail arrangements COMPOSE the existing rail vocabularies rather
 * than reinvent them: list columns are `ListColumn` (list-view.ts), the map
 * fragment is a `MapView` (map-view.ts) with its criteria half stripped
 * (the lens's own criteria govern on every rail - one filterset, three
 * renderings), and the board rail's lanes are a pure read-only group-by of
 * properties items already carry (docs/LENSES.md §4; a lens cannot link a
 * flow, so there are no stage lanes to configure). Arrangement KEYS (sort,
 * group, lanes-by) are opaque strings, exactly like a board's saved Refine
 * sort: the vocabulary lives in the view layer, whose parsers are the gate -
 * an unknown key coerces to the default there, like a stale URL param.
 */

/** The rails a lens renders on, switched live at the surface (docs/LENSES.md
 *  §4): the checklist is the fourth (docs/CHECKLIST.md §3, 2026-09-07 - the
 *  rail that ticks; the list rail carries no acts). The portfolio rail, if
 *  adopted, joins here (its own design thread). */
export const LENS_RAILS = ['list', 'board', 'map', 'checklist'] as const;
export type { LensCustomGroup } from './custom-groups';
export type LensRail = (typeof LENS_RAILS)[number];

/**
 * How the SECTIONS of one grouping are arranged (docs/LENSES.md §6b, built
 * 2026-09-27): the direction their natural order runs in, a manual order, and
 * the ones taken out of the view. Section keys are the grouper's own (a lane
 * value id, a flow or parent id, `<flowId>:<stageKey>`), opaque here like
 * every arrangement key. `dir` null = the grouping's natural direction; an
 * empty `order` = the natural order; `hidden` keys nobody's rows fall in stay
 * put quietly (a group hidden last month keeps hiding when it fills again).
 */
export interface LensSectionArrangement {
  dir: 'asc' | 'desc' | null;
  order: string[];
  hidden: string[];
}

export const EMPTY_SECTION_ARRANGEMENT: LensSectionArrangement = {
  dir: null,
  order: [],
  hidden: [],
};

/** The list rail's arrangement: ordering, grouping, columns. `group` is the
 *  list rail's row grouping (the Work page's group-by, generalized to every
 *  lens - docs/LENSES.md §6); keys opaque, view-layer-parsed. */
export interface LensListArrangement {
  sort: string | null;
  dir: 'asc' | 'desc' | null;
  group: string | null;
  /** Ordered, deduped column config (`ListColumn`, one definition with a
   *  board's list layout). Empty = the view layer's default set. */
  columns: ListColumn[];
  /** PER GROUPING (keyed by the grouping's own key, opaque here), how that
   *  grouping's sections are arranged - so switching the grouping and back
   *  never loses how you had each one, the same rule the per-rail shapes
   *  follow. A grouping with nothing arranged has no entry. */
  sections: Record<string, LensSectionArrangement>;
}

/** The board rail's arrangement: lanes as READ-ONLY group-by of properties
 *  items already carry (a laneable attribute, assignee, group;
 *  docs/LENSES.md §4). No manual lanes, no flow link, no per-item lane
 *  crumbs - those are table (view) machinery. `groupBy` is the secondary
 *  within-lane grouping. */
export interface LensBoardArrangement {
  /** What the lanes mean; null = the view layer's default (the active axis). */
  lanesBy: string | null;
  groupBy: string | null;
}

/** The checklist rail's arrangement (docs/CHECKLIST.md §7, seat decided
 *  2026-09-10: the order sits on the VIEW, the board's mechanism). `order` is
 *  the manual rank of lines: a sparse `itemId -> fractional-index rank`
 *  (`fractional-indexing`, lexicographic), so one drag writes one line's
 *  rank, no rank shifts to make room, and two maintainers' drags merge. At
 *  read time ranked lines come first by rank, unranked lines tail by
 *  `createdAt` - a line nobody has dragged yet simply joins in creation
 *  order. Written by the maintainer's drag straight through the spine
 *  (`setLensChecklistOrder`), the one arrangement that persists on the
 *  gesture rather than through "Update lens" - a rank map is not URL state
 *  (LENSES.md law 4, amended for this rail).
 *
 *  `sort`, `dir` and `group` are the rail's own Sort by and Group by
 *  (docs/CHECKLIST.md §3.5, 2026-10-29), kept apart from the list rail's:
 *  `sort` null is Manual (the authored order above), any other key a reading
 *  over it; `group` null is no grouping. Keys opaque, view-layer-parsed. */
export interface LensChecklistArrangement {
  order: RankMap;
  sort: string | null;
  dir: 'asc' | 'desc' | null;
  group: string | null;
}

export interface LensPayload {
  /** The one filterset, evaluated through the OPENER's access on every rail
   *  (the shared search-criteria grammar - the same words the palette,
   *  /search, and the Work list speak). */
  criteria: Criterion[];
  criteriaOp: CriteriaOp;
  /** The lens's custom groups (docs/LENSES.md §6c), in their DEFINED order,
   *  the natural order their sections are shown in. Top level because two
   *  rails use them: the list rail's sections under the grouping `custom`,
   *  the board rail's lanes. A lens may have none. */
  customGroups: LensCustomGroup[];
  /** Per-rail arrangement (docs/LENSES.md §4): each rail keeps its own saved
   *  shape, so switching rails never loses how you had each one arranged. */
  list: LensListArrangement;
  board: LensBoardArrangement;
  checklist: LensChecklistArrangement;
  /** The map rail's arrangement: a `MapView` fragment (edge toggles, scopes,
   *  depth encodings). Its criteria half is STRIPPED at normalization - the
   *  lens's own `criteria` above govern every rail; storing a second
   *  filterset here would be the drift this composition exists to prevent. */
  map: MapView;
  /** The maintainer's suggested rail. The opener's last choice wins for them
   *  (law 5: per-person viewing state, stored off the log). */
  defaultRail: LensRail;
  /** Show closed items? A cross-cutting display flag, not a criterion
   *  (docs/VIEWS.md "Closed items"): closed items are hidden from the lens on
   *  every rail unless this is set. Default off. An ephemeral `?showClosed=1`
   *  URL override peeks past it without persisting. */
  showClosed: boolean;
}

export const DEFAULT_LENS_LIST: LensListArrangement = {
  sort: null,
  dir: null,
  group: null,
  columns: [],
  sections: {},
};

export const DEFAULT_LENS_BOARD: LensBoardArrangement = {
  lanesBy: null,
  groupBy: null,
};

export const DEFAULT_LENS_CHECKLIST: LensChecklistArrangement = {
  order: {},
  sort: null,
  dir: null,
  group: null,
};

/** The map fragment's default: the map rail's own defaults with the criteria
 *  half empty (owned by the lens top level). */
const DEFAULT_LENS_MAP: MapView = { ...normalizeView(undefined), criteria: [], criteriaOp: 'all' };

export const DEFAULT_LENS_VIEW: LensPayload = {
  criteria: [],
  criteriaOp: 'all',
  customGroups: [],
  list: DEFAULT_LENS_LIST,
  board: DEFAULT_LENS_BOARD,
  checklist: DEFAULT_LENS_CHECKLIST,
  map: DEFAULT_LENS_MAP,
  defaultRail: 'list',
  showClosed: false,
};

/** The name of a person's own birth lens - what `/work` is mounted on
 *  (docs/LENSES.md §6). Theirs to shape; the standard below is what it starts
 *  as and can be restored to. */
export const BIRTH_LENS_NAME = 'Work';

/** The STANDARD (docs/LENSES.md §6a): the platform's named, permanent lens that
 *  a person's Work is born as a copy of and can be restored to in one gesture.
 *  It is a real seeded row per org (`mintStandardLensTx`), owned by
 *  `automation:platform` - not a constant compiled into the read, because
 *  restore has to point back at something a person can see and name. */
export const STANDARD_LENS_NAME = 'Standard Work';

/** The standard's config: the items the opener is a party on (either seat,
 *  `SELF_VALUE` standing for the opener and every group they act through,
 *  bound when the lens is read, docs/SEARCH.md "Parties"), default
 *  arrangement otherwise. A person's Work is a byte-equal copy of it: nothing
 *  is substituted at copy time, so the copy shows each opener their own.
 *  This constant is the DEFINITION of what gets seeded, and the read layer's
 *  last-resort default if an org somehow has no standard row. The arrangement
 *  keys are opaque here as everywhere (the list vocabulary parses them):
 *  `touched` = the Work list's default sort, Last updated (Leon 2026-09-15;
 *  `viewed`, the viewer's private recency of attention, was the standard
 *  before - migration 0076 moved the seeded rows), `active` its default
 *  grouping (the default axis, Leon 2026-07-22). A change to this constant
 *  reaches only orgs minted after it; the seeded rows of existing orgs ride
 *  their own data migration. */
export const STANDARD_LENS_VIEW: LensPayload = {
  ...DEFAULT_LENS_VIEW,
  // Only the items where the opener holds a seat, directly or through a
  // group (2026-10-20): a person's Work is their own work, not everything they
  // can see. Migration 0109 moved the seeded rows.
  criteria: [{ field: 'party', values: [SELF_VALUE] }],
  list: {
    ...DEFAULT_LENS_LIST,
    sort: 'touched',
    group: 'active',
    // Active above Idle (Leon, 2026-09-27): a daily surface reads "what I am
    // on" before "the backlog", so the Activity grouping's sections run
    // against their canonical order. Migration 0083 moved the seeded rows.
    sections: { active: { dir: 'desc', order: [], hidden: [] } },
  },
};

/**
 * Do two lens configs say the same thing? Both sides normalize first, so a
 * stored payload from an older shape compares by MEANING, not by JSON shape -
 * and normalization builds its objects from literals in a fixed order, which
 * makes the serialization canonical. This is what makes deviation from the
 * standard computable (docs/LENSES.md §6a item 3: deviation is visible), and
 * what lets a restore that would change nothing refuse as `no_change`.
 */
export function lensPayloadsEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalizeLensView(a)) === JSON.stringify(normalizeLensView(b));
}

const opaqueKey = (v: unknown): string | null =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;

/** A list of opaque keys: strings only, trimmed, empties dropped, deduped in
 *  first-seen order (a manual order names a key once). */
function opaqueKeys(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const k of v) {
    const key = opaqueKey(k);
    if (key !== null && !out.includes(key)) out.push(key);
  }
  return out;
}

/** Nothing arranged: the entry a normalized payload leaves out. */
export function isEmptySectionArrangement(a: LensSectionArrangement): boolean {
  return a.dir === null && a.order.length === 0 && a.hidden.length === 0;
}

/** One grouping's section arrangement from stored/wire input. */
export function normalizeSectionArrangement(input: unknown): LensSectionArrangement {
  const s = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  return {
    dir: s.dir === 'asc' || s.dir === 'desc' ? s.dir : null,
    order: opaqueKeys(s.order),
    hidden: opaqueKeys(s.hidden),
  };
}

/** The per-grouping map: each entry through {@link normalizeSectionArrangement},
 *  empty entries dropped, keys in sorted order - so two payloads that arrange
 *  the same sections serialize the same (`lensPayloadsEqual` compares the
 *  canonical JSON). */
function normalizeSections(input: unknown): Record<string, LensSectionArrangement> {
  const out: Record<string, LensSectionArrangement> = {};
  if (!input || typeof input !== 'object' || Array.isArray(input)) return out;
  const entries = Object.entries(input as Record<string, unknown>)
    .map(([k, v]) => [k.trim(), normalizeSectionArrangement(v)] as const)
    .filter(([k, a]) => k.length > 0 && !isEmptySectionArrangement(a))
    .sort(([a], [b]) => a.localeCompare(b));
  for (const [k, a] of entries) out[k] = a;
  return out;
}

/** The saved arrangement of one grouping's sections (the empty one when the
 *  grouping has no entry). */
export function sectionArrangementOf(payload: LensPayload, group: string): LensSectionArrangement {
  return payload.list.sections[group] ?? EMPTY_SECTION_ARRANGEMENT;
}

/**
 * Coerce arbitrary (stored or wire) input into a valid `LensPayload` - the
 * same defence every view payload gets (normalizeBoardView / normalizeView),
 * so a payload from an older, migrated, or hand-edited row can never put a
 * bad value on a lens. Unknown fields drop, and each rail's arrangement
 * normalizes through its own vocabulary's validator where one exists
 * (columns via the list layout's, the map fragment via the map's).
 */
export function normalizeLensView(input: unknown): LensPayload {
  const o = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;

  const l = (o.list && typeof o.list === 'object' ? o.list : {}) as Record<string, unknown>;
  const list: LensListArrangement = {
    sort: opaqueKey(l.sort),
    dir: l.dir === 'asc' || l.dir === 'desc' ? l.dir : null,
    group: opaqueKey(l.group),
    // One column validator (the list layout's): dedupe by key, clamp widths.
    columns: normalizeListColumns(l.columns),
    sections: normalizeSections(l.sections),
  };

  const b = (o.board && typeof o.board === 'object' ? o.board : {}) as Record<string, unknown>;
  const board: LensBoardArrangement = {
    lanesBy: opaqueKey(b.lanesBy),
    groupBy: opaqueKey(b.groupBy),
  };

  // The checklist's rank map, through the one stored-order validator, and
  // its sort and grouping, opaque like the list rail's.
  const c = (o.checklist && typeof o.checklist === 'object' ? o.checklist : {}) as Record<
    string,
    unknown
  >;
  // A direction belongs to a sort: under Manual (no sort) there is none, so a
  // stored one is dropped and can never read as a divergence.
  const checklistSort = opaqueKey(c.sort);
  const checklist: LensChecklistArrangement = {
    order: normalizeRankMap(c.order),
    sort: checklistSort,
    dir: checklistSort !== null && (c.dir === 'asc' || c.dir === 'desc') ? c.dir : null,
    group: opaqueKey(c.group),
  };

  // The map fragment: the map rail's own normalizer, then the criteria half
  // stripped - the lens's top-level criteria are the ONE filterset (§4).
  const map: MapView = { ...normalizeView(o.map), criteria: [], criteriaOp: 'all' };

  return {
    criteria: normalizeCriteria(o.criteria),
    criteriaOp: o.criteriaOp === 'any' ? 'any' : 'all',
    customGroups: normalizeCustomGroups(o.customGroups),
    list,
    board,
    checklist,
    map,
    defaultRail: (LENS_RAILS as readonly unknown[]).includes(o.defaultRail)
      ? (o.defaultRail as LensRail)
      : 'list',
    showClosed: o.showClosed === true,
  };
}

/**
 * Merge a rank patch into a checklist order, returning the next config: a
 * view's checklist rail and a board's checklist layout, which both keep it
 * in `checklist.order`. A drag passes the one line it moved (or, when the
 * lines had to be given ranks for the first time, every line) as
 * `id -> rank`. A `null` rank clears a line's manual rank. Pure, so the write
 * spine and tests share one definition and a concurrent drag's keys merge
 * instead of clobber - the kanban's `withKanbanOrder` over the same
 * `mergeRankPatch` (domain/rank-order.ts).
 */
export function withChecklistOrder<P extends { checklist: { order: RankMap } }>(
  payload: P,
  patch: RankPatch,
): P {
  return {
    ...payload,
    checklist: { ...payload.checklist, order: mergeRankPatch(payload.checklist.order, patch) },
  };
}
